import { createContext, useContext, useState, useRef } from "react";
import LogManager from "../utils/logManager";
import { parseLambdaPayload } from "../utils/routerLambdaUtils";

const PreferenceContext = createContext({
  config: {},
  isLoaded: false,
  loadFailed: false,
  /**
   * HTTP status of the failed /preferences GET, or null when the failure carried no
   * status.
   *
   * Exists so consumers can tell an expected denial from a genuine outage, which
   * `loadFailed` alone cannot — it is set identically for 403, 5xx, timeout and
   * parse failures. A HIRE session is denied /preferences by design and should
   * degrade silently; an unreachable preference service is a real fault and must
   * stay visible.
   */
  loadFailureStatus: null,
  initialAutoStart: false,
  loadPreferences: async () => {},
  updateConfig: async () => {},
});

export const usePreference = () => useContext(PreferenceContext);

export const PreferenceProvider = ({ children }) => {
  const [config, setConfig] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadFailureStatus, setLoadFailureStatus] = useState(null);
  const initialAutoStartRef = useRef(undefined);

  const loadPreferences = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);
    LogManager.info("Calling /preferences API with action: get");
    try {
      const response = await fetch("/preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "get" }),
        signal: controller.signal,
      });

      if (!response.ok) {
        // Carry the status ON the error, not only inside its message. The catch
        // below needs it to distinguish an expected 403 from a real outage, and a
        // message string cannot be branched on without parsing English.
        const error = new Error(`/preferences GET failed with status ${response.status}`);
        error.status = response.status;
        throw error;
      }

      const result = await parseLambdaPayload(response, "/preferences GET");
      LogManager.info("Parsed /preferences payload:", JSON.stringify(result));
      if (initialAutoStartRef.current === undefined) {
        initialAutoStartRef.current = result.autoStart === true;
        LogManager.info("initialAutoStart captured as:", initialAutoStartRef.current);
      }
      setConfig(result);
      return result;
    } catch (error) {
      LogManager.error("Error loading preferences:", error);
      LogManager.metric("Preference_Call_Failed", 1, { operation: "get" });
      setConfig({});
      setLoadFailed(true);
      // null covers the failures with no HTTP status: network error, the 20s abort,
      // a JSON parse failure. Consumers read null as a genuine fault, which is the
      // fail-visible direction — only an explicit 403 counts as expected.
      setLoadFailureStatus(typeof error?.status === "number" ? error.status : null);
      return {};
    } finally {
      clearTimeout(timeoutId);
      setIsLoaded(true);
    }
  };

  const updateConfig = async (partial) => {
    const previous = { ...config };
    const merged = { ...config, ...partial };
    setConfig(merged);
    LogManager.info("Calling /preferences API with action: update, preferenceConfig:", JSON.stringify(partial));
    try {
      const response = await fetch("/preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update", preferenceConfig: partial }),
      });

      const result = await parseLambdaPayload(response, "/preferences UPDATE");
      LogManager.info("Parsed /preferences UPDATE payload:", JSON.stringify(result));
      setConfig({ ...merged, ...result });
      return result;
    } catch (error) {
      LogManager.error("Error updating preferences:", error);
      LogManager.metric("Preference_Call_Failed", 1, { operation: "update" });
      setConfig(previous);
      throw error;
    }
  };

  return (
    <PreferenceContext.Provider value={{ config, isLoaded, loadFailed, loadFailureStatus, initialAutoStart: initialAutoStartRef.current, loadPreferences, updateConfig }}>
      {children}
    </PreferenceContext.Provider>
  );
};

export default PreferenceContext;
